import { Checkbox } from "@/components/Checkbox";
import { HStack, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/lib/i18n";
import { memo, useCallback } from "react";
import type { FieldRenderInputProps } from "../../types";
import { getAttributeOptions } from "../../utils/option-utils";
import { BaseField, type BaseFieldProps } from "../fields/BaseField";
import { useMultiSelectField } from "./useMultiSelectCheckboxField";

/**
 * 複数選択チェックボックスフィールドコンポーネント
 * 複数の選択肢から複数の値を選択する機能を提供します
 */
export const MultiSelectCheckboxField = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    layout,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    const renderInput = useCallback(
      ({
        value: inputValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        error: fieldError,
        disabled: isDisabled,
        readOnly: isReadOnly,
        "data-testid": inputTestId,
      }: FieldRenderInputProps) => {
        const { t } = useTranslation("translation", {
          keyPrefix: "features.formFactory.multiSelectCheckboxField",
        });

        const { handleCheckboxChange, isValueSelected } = useMultiSelectField({
          value: inputValue,
          onChange: handleChange,
        });

        if (!field) {
          return <div />;
        }

        const options = getAttributeOptions(field);
        if (options.length === 0) {
          return (
            <Text variant="pSmall" className="text-neutral-muted-on-fill">
              {t("noOptionsAvailable")}
            </Text>
          );
        }

        return (
          <VStack data-testid={inputTestId}>
            {options.map((option) => {
              const isChecked = isValueSelected(option.value);
              const checkboxId = `${fieldId}-${option.value}`;

              return (
                <HStack key={option.value} className="items-center p-2">
                  <Checkbox
                    id={checkboxId}
                    checked={isChecked}
                    onCheckedChange={
                      isDisabled || isReadOnly
                        ? undefined
                        : (checked) =>
                            handleCheckboxChange(option.value, checked)
                    }
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={isDisabled || isReadOnly}
                    aria-invalid={!!fieldError}
                    aria-describedby={
                      fieldError ? `${fieldId}-error` : undefined
                    }
                    size={16}
                  />
                  <Spacer />
                  <Label
                    htmlFor={checkboxId}
                    className={cn(
                      "cursor-pointer text-label text-primary-on-neutral-fill",
                    )}
                  >
                    {option.label}
                  </Label>
                </HStack>
              );
            })}
          </VStack>
        );
      },
      [field, fieldId],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={readOnly}
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

MultiSelectCheckboxField.displayName = "MultiSelectCheckboxField";
